import React from 'react';
import styled, { css, useTheme } from 'styled-components';

import { Theme } from '~/theme';

import { measureTextWidth } from '~/util/canvasDrawers';
import useHover from '~/components/util/useHover';

import TEST_ID from './index.testid';
import Tooltip from '~/components/Tooltip';

type Props = {
  /** The total width this component is allowed to take up */
  width: number;
  tagList: Array<string>;
};
const TagColumnCell: React.FC<Props> = ({ tagList, width }) => {
  const theme = useTheme();
  const [showTooltip, tooltipProps] = useHover();

  const tagsToShow: Array<string> = [];
  const tagsToShowInTooltip: Array<string> = [];

  /** Always make sure we have enough space to put the extra tags label */
  const extraTagsLabelWidth =
    measureTextWidth(`+${tagList.length}`, getFontSize(theme)) +
    getMarginSize(theme);

  let currentWidth = extraTagsLabelWidth;
  for (const tag of tagList) {
    const tagWidth =
      measureTextWidth(tag, getFontSize(theme)) + 2 * getPaddingSize(theme);

    if (currentWidth !== extraTagsLabelWidth) {
      // is not the first, so add margin
      currentWidth += getMarginSize(theme);
    }
    currentWidth += tagWidth;

    if (currentWidth > width) {
      tagsToShowInTooltip.push(tag);
    } else {
      tagsToShow.push(tag);
    }
  }

  let tagCounterComponent;
  if (tagList.length > tagsToShow.length) {
    tagCounterComponent = (
      <ExtraTagsCounter data-testid={TEST_ID.EXTRA_TAG_COUNTER}>
        +{tagList.length - tagsToShow.length}
      </ExtraTagsCounter>
    );
  }

  let tooltipComponent;
  if (showTooltip && tagsToShowInTooltip.length > 0) {
    tooltipComponent = (
      <StyledTooltip>
        <TagsInTooltipContainer>
          {tagsToShowInTooltip.map((tag, idx) => (
            <InTooltipTagElement key={`${idx}-${tag}`}>
              {tag}
            </InTooltipTagElement>
          ))}
        </TagsInTooltipContainer>
      </StyledTooltip>
    );
  }

  return (
    <Container {...tooltipProps}>
      <TagCellContainer width={width}>
        {tagsToShow.map((tag, idx) => (
          <TagElement key={`${idx}-${tag}`}>{tag}</TagElement>
        ))}
        {tagCounterComponent}
      </TagCellContainer>
      {tooltipComponent}
    </Container>
  );
};

const getFontSize = (theme: Theme): number =>
  theme.remToPxRaw(theme.fontSize('s'));
const getMarginSize = (theme: Theme): number =>
  theme.remToPxRaw(theme.space('xxs'));
const getPaddingSize = (theme: Theme): number =>
  theme.remToPxRaw(theme.space('xxs'));

type TagCellContainerProps = {
  width: number;
};
const TagCellContainer = styled.div<TagCellContainerProps>`
  white-space: nowrap;
  overflow-x: hidden;

  ${({ width, theme }) => css`
    max-width: ${width}px;
    font-size: ${getFontSize(theme)}px;

    > :not(:first-child) {
      margin-left: ${getMarginSize(theme)}px;
    }
  `}
`;

const Container = styled.div<{}>`
  display: inline-block;
  width: 100%;
`;

const ExtraTagsCounter = styled.span<{}>(
  ({ theme }) => css`
    color: ${theme.color('text', 'light')};
    margin-left: ${getMarginSize(theme)}px;
  `,
);

const TagElement = styled.div<{}>(
  ({ theme }) => css`
    display: inline-block;
    color: ${theme.color('primary', 'light')};

    border: ${theme.getTokens().border.width.s} solid
      ${theme.color('primary', 'light')};
    border-radius: ${theme.getTokens().border.radius.s};

    padding: ${theme.space('xxxs')} ${getPaddingSize(theme)}px;
  `,
);

const TagsInTooltipContainer = styled.div<{}>`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-content: space-around;
  max-width: 300px;
  overflow-x: hidden;

  ${({ theme }) => css`
    margin-left: -${getMarginSize(theme)}px;
    margin-top: -${getMarginSize(theme)}px;
  `};
`;

const InTooltipTagElement = styled(TagElement)<{}>(
  ({ theme }) => css`
    margin-left: ${getMarginSize(theme)}px;
    margin-top: ${getMarginSize(theme)}px;
    color: ${theme.color('white')};
    border-color: ${theme.color('white')};
  `,
);

const StyledTooltip = styled(Tooltip)<{}>(
  ({ theme }) => css`
    font-size: ${getFontSize(theme)}px;
  `,
);

export default TagColumnCell;
